import { ContractorTransfer, ContractorTransferItemEntityTypeEnum, ContractorTransferItemStatusEnum, ContractorTransferStageEnum, ContractorTransferStatusEnum } from 'src/api'

// Pure presentation helpers for contractor transfers: no React or MUI imports, so jest can test them directly

export type TransferChipColor = 'default' | 'info' | 'success' | 'warning' | 'error'

export interface TransferStatusMeta {
  label: string
  color: TransferChipColor
  description?: string
}

export const TRANSFER_POLL_INTERVAL_MS = 5000

const FINAL_STATUSES: string[] = [
  ContractorTransferStatusEnum.Completed,
  ContractorTransferStatusEnum.CompletedWithWarnings,
  ContractorTransferStatusEnum.Failed,
  ContractorTransferStatusEnum.Cancelled
]

export const isTransferFinal = (status?: string | null): boolean => Boolean(status && FINAL_STATUSES.includes(status))

/** The status page polls a running transfer and stops once its status is final. */
export const getTransferPollInterval = (status?: string | null): number | false => (isTransferFinal(status) ? false : TRANSFER_POLL_INTERVAL_MS)

/** A retry the backend accepted: the row's `updatedAt` at that moment and when it happened (client clock). */
export interface TransferRetryMark {
  updatedAt?: string
  at: number
}

export const TRANSFER_RETRY_WATCH_MS = 10 * 60 * 1000

/**
 * A retried transfer keeps its final status until the job picks it up, so after a retry the page keeps polling until the
 * row changes (compared by the server's own `updatedAt`, so clock skew does not matter), for at most ten minutes.
 */
export const getTransferRefetchInterval = (
  transfer: Pick<ContractorTransfer, 'status' | 'updatedAt'> | undefined,
  retry: TransferRetryMark | null,
  now: number = Date.now()
): number | false => {
  const waitingForRetry = Boolean(retry && transfer && transfer.updatedAt === retry.updatedAt && now - retry.at < TRANSFER_RETRY_WATCH_MS)

  return waitingForRetry ? TRANSFER_POLL_INTERVAL_MS : getTransferPollInterval(transfer?.status)
}

export const getTransferStatusMeta = (status?: string | null): TransferStatusMeta => {
  switch (status) {
    case ContractorTransferStatusEnum.Queued:
      return { label: 'Queued', color: 'default', description: 'Waiting for the transfer job to start' }
    case ContractorTransferStatusEnum.Freezing:
      return { label: 'Freezing', color: 'info', description: 'Projects are frozen and moved users are signed out; the data moves after a one-minute pause' }
    case ContractorTransferStatusEnum.Committed:
      return { label: 'Moved', color: 'info', description: 'The data has moved; the readings are rewritten next' }
    case ContractorTransferStatusEnum.ReadingsRunning:
      return { label: 'Rewriting readings', color: 'info', description: 'Sensor readings are being re-stamped with the target contractor' }
    case ContractorTransferStatusEnum.Healing:
      return { label: 'Healing', color: 'info', description: 'Waiting for the heal sweep, which runs 15 minutes after the move' }
    case ContractorTransferStatusEnum.Verifying:
      return { label: 'Verifying', color: 'info', description: 'Checking that no moved row still carries the source contractor' }
    case ContractorTransferStatusEnum.Completed:
      return { label: 'Completed', color: 'success' }
    case ContractorTransferStatusEnum.CompletedWithWarnings:
      return { label: 'Completed with warnings', color: 'warning', description: 'Some rows were left with the source contractor; see the warnings' }
    case ContractorTransferStatusEnum.Failed:
      return { label: 'Failed', color: 'error' }
    case ContractorTransferStatusEnum.Cancelled:
      return { label: 'Cancelled', color: 'default' }
    default:
      return { label: status || '-', color: 'default' }
  }
}

export type TransferStageState = 'pending' | 'active' | 'done' | 'failed' | 'skipped'

export interface TransferStage {
  stage: ContractorTransferStageEnum
  label: string
  state: TransferStageState
}

const STAGES: { stage: ContractorTransferStageEnum; label: string }[] = [
  { stage: ContractorTransferStageEnum.Relational, label: 'Relational move' },
  { stage: ContractorTransferStageEnum.Readings, label: 'Readings rewrite' },
  { stage: ContractorTransferStageEnum.Heal, label: 'Heal' },
  { stage: ContractorTransferStageEnum.Verify, label: 'Verification' }
]

// How far a running transfer got: the stages before the index are done, the one at the index is active
const PROGRESS_BY_STATUS: Record<string, { done: number; active: boolean }> = {
  [ContractorTransferStatusEnum.Queued]: { done: 0, active: false },
  [ContractorTransferStatusEnum.Freezing]: { done: 0, active: true },
  [ContractorTransferStatusEnum.Committed]: { done: 1, active: false },
  [ContractorTransferStatusEnum.ReadingsRunning]: { done: 1, active: true },
  [ContractorTransferStatusEnum.Healing]: { done: 2, active: true },
  [ContractorTransferStatusEnum.Verifying]: { done: 3, active: true },
  [ContractorTransferStatusEnum.Completed]: { done: 4, active: false },
  [ContractorTransferStatusEnum.CompletedWithWarnings]: { done: 4, active: false }
}

/**
 * The popup after Run or Retry. The backend answers 202 even when the job could not be queued: the transfer is then
 * kept as failed, ready for a retry, and the popup says so instead of reporting it queued.
 */
export const getTransferQueuedMessage = (transfer: Pick<ContractorTransfer, 'id' | 'status' | 'error'>, retried = false): { text: string; type: 'success' | 'warning' } =>
  transfer.status === ContractorTransferStatusEnum.Failed
    ? { text: `Transfer #${transfer.id} was not queued: ${transfer.error || 'the job could not be queued'}. Retry it from its page.`, type: 'warning' }
    : { text: retried ? `Transfer #${transfer.id} was queued again` : `Transfer #${transfer.id} is queued`, type: 'success' }

export const getTransferStageLabel = (stage?: string | null): string => STAGES.find((item) => item.stage === stage)?.label || 'Queueing'

export interface TransferErrorRow {
  id: number
  at: string
  stage: string
  message: string
}

/** Every failure the transfer recorded, newest first; one a retry recovered from stays in the list. */
export const getTransferErrorRows = (transfer: Pick<ContractorTransfer, 'errors'>): TransferErrorRow[] =>
  (transfer.errors || []).map((error, index) => ({ id: index, at: error.at, stage: getTransferStageLabel(error.stage), message: error.message })).reverse()

export const getTransferStages = ({ status, stage }: Pick<ContractorTransfer, 'status' | 'stage'>): TransferStage[] => {
  if (status === ContractorTransferStatusEnum.Cancelled) {
    return STAGES.map((item) => ({ ...item, state: 'skipped' }))
  }

  if (status === ContractorTransferStatusEnum.Failed) {
    // A failure without a stage happened before the move started
    const failedIndex = Math.max(
      STAGES.findIndex((item) => item.stage === stage),
      0
    )

    return STAGES.map((item, index) => ({ ...item, state: index < failedIndex ? 'done' : index === failedIndex ? 'failed' : 'pending' }))
  }

  const progress = PROGRESS_BY_STATUS[status] ?? { done: 0, active: false }

  return STAGES.map((item, index) => ({
    ...item,
    state: index < progress.done ? 'done' : index === progress.done && progress.active ? 'active' : 'pending'
  }))
}

/** Mirrors the backend: a failed transfer resumes from its progress, one completed with warnings re-runs the heal and the verification. */
export const canRetryTransfer = ({ status }: Pick<ContractorTransfer, 'status'>): boolean =>
  status === ContractorTransferStatusEnum.Failed || status === ContractorTransferStatusEnum.CompletedWithWarnings

/** Mirrors the backend: only a queued transfer, or one that failed before its relational move committed, can be cancelled. */
export const canCancelTransfer = ({ status, stage, committedAt }: Pick<ContractorTransfer, 'status' | 'stage' | 'committedAt'>): boolean =>
  status === ContractorTransferStatusEnum.Queued || (status === ContractorTransferStatusEnum.Failed && stage === ContractorTransferStageEnum.Relational && !committedAt)

const ITEM_TYPE_LABELS: Record<string, string> = {
  [ContractorTransferItemEntityTypeEnum.Project]: 'Project',
  [ContractorTransferItemEntityTypeEnum.UserContractor]: 'User',
  [ContractorTransferItemEntityTypeEnum.Sensor]: 'Sensor',
  [ContractorTransferItemEntityTypeEnum.Gateway]: 'Gateway',
  [ContractorTransferItemEntityTypeEnum.WagnerProbe]: 'Wagner probe',
  [ContractorTransferItemEntityTypeEnum.DataLogger]: 'Data logger'
}

export const getTransferItemTypeLabel = (entityType?: string | null): string => (entityType && ITEM_TYPE_LABELS[entityType]) || entityType || '-'

/** `users_contractors` → `Users contractors`: the counts are keyed by table or by step name. */
export const formatTransferCountLabel = (key: string): string => {
  const words = key.split('_').filter(Boolean).join(' ')
  return words ? `${words.charAt(0).toUpperCase()}${words.slice(1)}` : key
}

export interface ReadingsProgressSummary {
  tables: number
  finishedTables: number
  rows: number
}

type ReadingsTableProgress = { updated?: number; finished?: boolean }

/** Totals of the readings rewrite, which stores its progress per hypertable under `progress.readings`. */
export const summarizeReadingsProgress = (progress?: object | null): ReadingsProgressSummary | null => {
  const readings = (progress as { readings?: Record<string, ReadingsTableProgress> } | null | undefined)?.readings
  if (!readings || typeof readings !== 'object') return null

  const tables = Object.values(readings).filter((table): table is ReadingsTableProgress => Boolean(table) && typeof table === 'object')

  return {
    tables: tables.length,
    finishedTables: tables.filter((table) => table.finished).length,
    rows: tables.reduce((sum, table) => sum + (Number(table.updated) || 0), 0)
  }
}

export const getTransferItemStatusMeta = (status?: string | null): TransferStatusMeta => {
  switch (status) {
    case ContractorTransferItemStatusEnum.Done:
      return { label: 'Moved', color: 'success' }
    case ContractorTransferItemStatusEnum.Skipped:
      return { label: 'Skipped', color: 'warning' }
    case ContractorTransferItemStatusEnum.Pending:
      return { label: 'Pending', color: 'default' }
    default:
      return { label: status || '-', color: 'default' }
  }
}
